import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { Session } from '../../api/session';
import { createPass, createPassRequest, getObject, getObjectAddresses, getVehicleModels, type NewPass, type ObjectInfo } from '../../api/tenant';
import { passOptions, type Mode } from '../../domain/availability';
import { crewNames, crewPlates, crewPurpose, crewShareMessage, MAX_CREW_CARS, passesWord } from '../../domain/crew';
import { formatServerDate, startOfMoscowDay } from '../../domain/moscowTime';
import { describePassError } from '../../domain/passErrors';
import { GuestType } from '../../domain/passes';
import { normalizePlate } from '../../domain/plate';
import { passWindow, type PassDuration, type When } from '../../domain/schedule';
import type { TelegramApi } from '../../telegram/webapp';
import { Chips, Segmented } from '../../ui/Choice';
import { Skeleton } from '../../ui/Skeleton';
import { MessageScreen } from '../auth/screens';
import { LoadFailed } from '../common/LoadFailed';
import { UntilField } from '../common/UntilField';
import { windowCaption } from '../common/windowCaption';
import s from './passForm.module.css';

interface Props {
  session: Session;
  telegram: TelegramApi;
  object: ObjectInfo;
}

const WHEN_CHIPS: Array<{ id: 'now' | 'today' | 'tomorrow' | 'date'; label: string }> = [
  { id: 'now', label: 'Сейчас' },
  { id: 'today', label: 'Сегодня' },
  { id: 'tomorrow', label: 'Завтра' },
  { id: 'date', label: 'Дата…' },
];

type CarResult = { plate: string; ok: boolean; error?: string };

/**
 * «Пропуск бригаде» (ideas 2026-10-13, «Подрядчики и стройка»): several cars — or several people on foot (#4216) —
 * one period, one comment for security. The API has no group pass, so each car or person gets its own temporary
 * pass (or approval request) with the same window.
 */
export function CrewScreen({ session, telegram, object }: Props) {
  const queryClient = useQueryClient();
  const addresses = useQuery({ queryKey: ['addresses', object.id], queryFn: () => getObjectAddresses(session, object.id) });
  const details = useQuery({ queryKey: ['object', object.id], queryFn: () => getObject(session, object.id) });
  const requireModel = details.data?.settings.passesRequireModel === true;
  const models = useQuery({ queryKey: ['vehicle-models'], queryFn: () => getVehicleModels(session), enabled: requireModel, staleTime: Infinity });

  const opts = useMemo(() => passOptions(details.data?.settings ?? {}), [details.data]);
  const [params] = useSearchParams();
  const walkersAllowed = opts.durations('pedestrian').length > 0;
  const carsAllowed = opts.durations('vehicle').length > 0;
  const [kindPick, setKind] = useState<'vehicle' | 'pedestrian'>(params.get('kind') === 'pedestrian' ? 'pedestrian' : 'vehicle');
  const kind = kindPick === 'pedestrian' ? (walkersAllowed ? 'pedestrian' : 'vehicle') : carsAllowed ? 'vehicle' : 'pedestrian';
  const walkers = kind === 'pedestrian';
  const usable = useMemo(() => (addresses.data ?? []).filter((a) => !a.isBlocked), [addresses.data]);
  // A crew works for days: temporary when the object allows it for cars, otherwise a one-day pass.
  const durationItem = useMemo(() => {
    const list = opts.durations(kind);
    return list.find((d) => d.duration === 'temporary') ?? list[0] ?? null;
  }, [opts, kind]);
  const purposes = details.data?.options ?? [];
  const vehicleTypes = details.data?.vehicleTypes ?? [];

  const [addressId, setAddressId] = useState<number | null>(null);
  const [plates, setPlates] = useState<string[]>(() => [normalizePlate(params.get('plate') ?? ''), '']);
  const [names, setNames] = useState<string[]>(['', '']);
  const [carModels, setCarModels] = useState<string[]>(['', '']);
  const [vehicleTypeId, setVehicleTypeId] = useState<number | null>(null);
  const [whenId, setWhenId] = useState<'now' | 'today' | 'tomorrow' | 'date'>('now');
  const [date, setDate] = useState('');
  const [until, setUntil] = useState('');
  const [purposeId, setPurposeId] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<{ mode: Mode; cars: CarResult[]; startsAt: Date | null; expiresAt: Date | null; walkers: boolean } | null>(null);

  useEffect(() => {
    if (addressId === null && usable.length) setAddressId(usable[0].id);
  }, [usable, addressId]);
  // The object's crew-like purpose is preselected once; the resident may clear or change it.
  const purposePicked = useRef(false);
  useEffect(() => {
    if (purposePicked.current || !details.data) return;
    purposePicked.current = true;
    setPurposeId(crewPurpose(details.data.options ?? [])?.id ?? null);
  }, [details.data]);

  const duration: PassDuration = durationItem?.duration ?? 'temporary';
  const mode: Mode = durationItem?.mode ?? 'direct';
  const limitKey = duration === 'temporary' ? 'passesLimitHoursTemporary' : 'passesLimitHoursOneTime';
  const limitRaw = details.data?.settings[limitKey];
  const limitHours = typeof limitRaw === 'number' && limitRaw > 0 ? limitRaw : null;
  const now = new Date();
  const when: When = whenId === 'date' ? (date ? { date } : 'today') : whenId;
  const window = passWindow({ when, duration, limitHours, now, forRequest: mode === 'request', until });
  const ready = walkers ? crewNames(names) : crewPlates(plates);

  const submit = useMutation({
    mutationFn: async () => {
      const cars: CarResult[] = [];
      // One by one: a refusal for one car (a typo in the plate) must not cost the others their pass.
      for (const plate of ready) {
        const index = plates.findIndex((p) => normalizePlate(p) === plate);
        const model = walkers ? undefined : models.data?.find((m) => m.title === carModels[index]);
        const body: NewPass = {
          addressId: addressId!,
          durationType: duration === 'temporary' ? 2 : 1,
          guestType: walkers ? GuestType.Pedestrian : GuestType.Vehicle,
          guestData: walkers
            ? { name: plate }
            : { plateNumber: plate, ...(model ? { modelId: model.id } : {}), ...(vehicleTypeId ? { vehicleType: vehicleTypeId } : {}) },
          ...(window.startsAt ? { startsAt: formatServerDate(window.startsAt) } : {}),
          ...(window.expiresAt ? { expiresAt: formatServerDate(window.expiresAt) } : {}),
          ...(comment.trim() ? { comment: comment.trim() } : {}),
          ...(purposeId ? { options: [purposeId] } : {}),
        };
        try {
          if (mode === 'request') await createPassRequest(session, body);
          else await createPass(session, body);
          cars.push({ plate, ok: true });
        } catch (e) {
          const why = describePassError(e);
          cars.push({ plate, ok: false, error: (walkers ? why.fields.name : why.fields.plate) ?? why.general ?? 'Не удалось оформить' });
        }
      }
      return cars;
    },
    onSuccess: (cars) => {
      telegram.notify(cars.every((c) => c.ok) ? 'success' : 'error');
      void queryClient.invalidateQueries({ queryKey: ['passes'] });
      setResults({ mode, cars, startsAt: window.startsAt, expiresAt: window.expiresAt, walkers });
    },
  });

  if (addresses.isError || details.isError) {
    return <LoadFailed error={addresses.error ?? details.error} onRetry={() => { void addresses.refetch(); void details.refetch(); }} fullScreen />;
  }
  if (!addresses.data || !details.data) {
    return (
      <main className={s.sheet} aria-busy="true">
        <Skeleton height={44} />
        <Skeleton height={120} />
      </main>
    );
  }
  if (!usable.length) {
    return <MessageScreen title="Заказ недоступен" text="Адрес заблокирован управляющей компанией. Позвоните в УК, чтобы узнать причину." />;
  }
  if (!durationItem) {
    return <MessageScreen title="Пропуск бригаде недоступен" text="На этом объекте жители не оформляют такие пропуска сами. Обратитесь в УК." />;
  }

  if (results) {
    const ok = results.cars.filter((c) => c.ok);
    const failed = results.cars.filter((c) => !c.ok);
    const address = addresses.data.find((a) => a.id === addressId);
    return (
      <main className={s.done}>
        <h1 className={s.doneTitle}>
          {results.mode === 'request' ? 'Заявки отправлены' : failed.length ? `Оформлено ${ok.length} из ${results.cars.length}` : 'Пропуска оформлены'}
        </h1>
        <ul className={s.crewResults} aria-label={results.walkers ? 'Люди бригады' : 'Машины бригады'}>
          {results.cars.map((c) => (
            <li key={c.plate} className={c.ok ? s.crewOk : s.crewFail}>
              <b>{c.plate}</b> {c.ok ? (results.mode === 'request' ? '— на согласовании в УК' : '— пропуск оформлен') : `— ${c.error}`}
            </li>
          ))}
        </ul>
        <p className={s.doneText}>{windowCaption(duration, results.startsAt, results.expiresAt, new Date())}</p>
        {ok.length > 0 && results.mode !== 'request' && results.startsAt && results.expiresAt && (
          <button
            type="button"
            className={s.submit}
            onClick={() =>
              telegram.share(
                telegram.appLink(),
                crewShareMessage({ objectName: object.name, address: address?.name ?? '', plates: ok.map((c) => c.plate), startsAt: results.startsAt!, expiresAt: results.expiresAt!, walkers: results.walkers }),
              )
            }
          >
            Отправить прорабу
          </button>
        )}
        <Link to="/" className={ok.length && results.mode !== 'request' ? s.secondary : s.submit}>
          На главную
        </Link>
      </main>
    );
  }

  const setPlate = (i: number, v: string) => setPlates((list) => list.map((p, n) => (n === i ? normalizePlate(v) : p)));
  const removeCar = (i: number) => {
    setPlates((list) => list.filter((_, n) => n !== i));
    setCarModels((list) => list.filter((_, n) => n !== i));
  };
  const onSubmit = () => {
    if (!ready.length) return setError(walkers ? 'Введите хотя бы одно ФИО' : 'Введите хотя бы один госномер');
    if (!walkers && vehicleTypes.length && !vehicleTypeId) return setError('Выберите тип транспорта');
    if (whenId === 'date' && !date) return setError('Выберите дату начала работ');
    setError(null);
    submit.mutate();
  };

  return (
    <main className={s.sheet} aria-labelledby="crew-title">
      <header className={s.head}>
        <h1 id="crew-title" className={s.title}>
          Пропуск бригаде
        </h1>
        <p className={s.where}>Подрядчики и стройка: {walkers ? 'несколько человек' : 'несколько машин'} на несколько дней</p>
      </header>

      {walkersAllowed && carsAllowed && (
        <Segmented
          label="Кто приедет"
          value={kind}
          items={[{ id: 'vehicle', label: 'Машины' }, { id: 'pedestrian', label: 'Пешеходы' }]}
          onChange={(v) => { setKind(v); setError(null); }}
        />
      )}

      {addresses.data.length > 1 && (
        <Chips
          label="Адрес"
          value={addressId}
          items={addresses.data.map((a) => ({ id: a.id, label: a.isBlocked ? `${a.name} — заблокирован` : a.name, disabled: a.isBlocked }))}
          onChange={(v) => v !== null && setAddressId(v)}
        />
      )}

      {walkers ? (
        <div className={s.field}>
          <span className={s.label}>Люди бригады</span>
          {names.map((n, i) => (
            <div key={i} className={s.crewRow}>
              <input
                aria-label={`ФИО ${i + 1}`}
                className={s.input}
                value={n}
                maxLength={255}
                onChange={(e) => setNames((list) => list.map((x, k) => (k === i ? e.target.value : x)))}
                placeholder="Фамилия Имя"
                autoComplete="off"
              />
              {names.length > 1 && (
                <button type="button" className={s.crewRemove} aria-label={`Убрать человека ${i + 1}`} onClick={() => setNames((l) => l.filter((_, k) => k !== i))}>
                  ×
                </button>
              )}
            </div>
          ))}
          {names.length < MAX_CREW_CARS && (
            <button type="button" className={s.more} onClick={() => setNames((l) => [...l, ''])}>
              + Ещё человек
            </button>
          )}
          <span className={s.hint}>Каждому — свой пропуск, охрана пропустит по имени</span>
        </div>
      ) : (
      <div className={s.field}>
        <span className={s.label}>Машины бригады</span>
        {plates.map((p, i) => (
          <div key={i} className={s.crewRow}>
            <input
              aria-label={`Госномер машины ${i + 1}`}
              className={s.plateSmall}
              value={p}
              onChange={(e) => setPlate(i, e.target.value)}
              placeholder="А000АА77"
              autoComplete="off"
              autoCapitalize="characters"
            />
            {requireModel && (
              <input
                aria-label={`Модель машины ${i + 1}`}
                list="crew-models"
                className={s.input}
                value={carModels[i] ?? ''}
                onChange={(e) => setCarModels((list) => list.map((m, n) => (n === i ? e.target.value : m)))}
                placeholder="Модель"
              />
            )}
            {plates.length > 1 && (
              <button type="button" className={s.crewRemove} aria-label={`Убрать машину ${i + 1}`} onClick={() => removeCar(i)}>
                ×
              </button>
            )}
          </div>
        ))}
        {requireModel && (
          <datalist id="crew-models">
            {(models.data ?? []).map((m) => (
              <option key={m.id} value={m.title} />
            ))}
          </datalist>
        )}
        {plates.length < MAX_CREW_CARS && (
          <button type="button" className={s.more} onClick={() => { setPlates((l) => [...l, '']); setCarModels((l) => [...l, '']); }}>
            + Ещё машина
          </button>
        )}
        <span className={s.hint}>Можно печатать латиницей — заменим на кириллицу</span>
      </div>
      )}

      {!walkers && vehicleTypes.length > 0 && (
        <div className={s.field}>
          <span className={s.label}>Тип транспорта</span>
          <Chips label="Тип транспорта" value={vehicleTypeId} items={vehicleTypes.map((t) => ({ id: t.id, label: t.name }))} onChange={setVehicleTypeId} />
        </div>
      )}

      <div className={s.field}>
        <span className={s.label}>Начало работ</span>
        <Chips label="Начало работ" value={whenId} items={WHEN_CHIPS} onChange={(v) => v && setWhenId(v)} />
        {whenId === 'date' && (
          <input
            type="date"
            aria-label="Дата начала работ"
            className={s.input}
            value={date}
            min={formatServerDate(startOfMoscowDay(now)).slice(0, 10)}
            onChange={(e) => setDate(e.target.value)}
          />
        )}
      </div>
      {duration === 'temporary' && <UntilField value={until} onChange={setUntil} startsAt={window.startsAt} expiresAt={window.expiresAt} limitHours={limitHours} />}
      <p className={s.caption}>{windowCaption(duration, window.startsAt, window.expiresAt, now)}</p>

      {purposes.length > 0 && (
        <div className={s.field}>
          <span className={s.label}>Цель визита · необязательно</span>
          <Chips label="Цель визита" value={purposeId} items={purposes.map((p) => ({ id: p.id, label: p.name }))} onChange={setPurposeId} allowNone limit={5} />
        </div>
      )}

      <div className={s.field}>
        <label htmlFor="crew-comment" className={s.label}>
          Комментарий для охраны
        </label>
        <textarea id="crew-comment" className={s.textarea} value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Бригада, прораб и его телефон" />
      </div>

      <div className={s.footer}>
        {error && (
          <p className={s.error} role="alert">
            {error}
          </p>
        )}
        <button type="button" className={s.submit} onClick={onSubmit} disabled={submit.isPending}>
          {submit.isPending
            ? 'Оформляем…'
            : mode === 'request'
              ? `Отправить на согласование${ready.length > 1 ? ` (${ready.length})` : ''}`
              : ready.length > 1
                ? `Оформить ${ready.length} ${passesWord(ready.length)}`
                : 'Оформить пропуск'}
        </button>
      </div>
    </main>
  );
}
